import StepsHeroSection from "../components/landing/StepsHeroSection"
import FilingChecklistSection from "../components/landing/FilingChecklistSection"
import FeeScheduleSection from "../components/landing/FeeScheduleSection"
import LandingTextSection from "../components/landing/LandingTextSection"
import InstantFilingSection from "../components/landing/InstantFilingSection"
import Footer from "../components/ui/Footer"
import { useSiteContent } from "../hooks/useSiteContent"

const DEFAULT_STEPS = [
  {
    title: "Application information",
    text: "Provide the applicant's name and postal address.",
  },
  {
    title: "Design representation",
    text: "Upload the representation of your design, up to 7 views or perspectives.",
  },
  {
    title: "Design details",
    text: "Provide the information required to identify and represent your design.",
  },
  {
    title: "Classification",
    text: "Select or provide the relevant classification group for your design.",
  },
  {
    title: "Registration",
    text: "Review your application and submit it for official filing.",
  },
]

const DEFAULT_CHECKLIST_ITEMS = [
  "Applicant information: name and postal address",
  "Reproduction of design: up to 7 views or perspectives",
  "Group applications: information for multiple designs, where applicable",
  "Additional requests: any additional requests relating to the application",
]

const DEFAULT_SERVICE_CARDS = [
  {
    title: "Trademark",
    image: "/images/trademark.svg",
    text: "Protect your brand throughout the European Union.",
    path: "/trademark",
  },
  {
    title: "Design",
    image: "/images/design.svg",
    text: "Protect the appearance of your products throughout the European Union.",
    path: "/design",
  },
  {
    title: "Patent",
    image: "/images/patent.svg",
    text: "Protect your technical inventions through the European patent system.",
    path: "/patent",
  },
]

export default function DesignPage() {
  const site = useSiteContent("design")

  const steps = DEFAULT_STEPS.map((step, index) => ({
    title: site.get(`design.hero.step.${index}.title`, step.title),
    text: site.get(`design.hero.step.${index}.text`, step.text),
  }))

  const checklistItems = DEFAULT_CHECKLIST_ITEMS.map((item, index) =>
    site.get(`design.checklist.item.${index}`, item)
  )

  const serviceCards = DEFAULT_SERVICE_CARDS.map((card, index) => ({
    ...card,
    title: site.get(`design.services.card.${index}.title`, card.title),
    text: site.get(`design.services.card.${index}.text`, card.text),
  }))

  return (
    <div className="flex min-h-screen flex-col">
      {site.isAdmin && (
        <button
          type="button"
          onClick={() => site.setEditMode(!site.editMode)}
          className={`fixed bottom-6 right-6 z-50 rounded-full border-2 border-white px-5 py-3 text-sm font-semibold shadow-xl transition ${
            site.editMode ? "bg-orange-500 text-white" : "bg-dantzBlue text-white"
          }`}
        >
          {site.editMode ? "Done editing" : "Edit page"}
        </button>
      )}

      <StepsHeroSection
        backgroundImage="/images/design.svg"
        heading={site.get("design.hero.heading", "EU DESIGN")}
        description={site.get(
          "design.hero.description",
          "File your EU design online and protect the appearance of your products throughout the European Union. xpress-ip provides a streamlined filing process combined with professional representation before the EUIPO."
        )}
        steps={steps}
        editable={site.editMode}
        onSaveHeading={(value) => site.save("design.hero.heading", value)}
        onSaveDescription={(value) => site.save("design.hero.description", value)}
        onSaveStepTitle={(index, value) => site.save(`design.hero.step.${index}.title`, value)}
        onSaveStepText={(index, value) => site.save(`design.hero.step.${index}.text`, value)}
      />

      <FilingChecklistSection
        title={site.get("design.checklist.title", "FILING CHECKLIST")}
        items={checklistItems}
        editable={site.editMode}
        onSaveTitle={(value) => site.save("design.checklist.title", value)}
        onSaveItemText={(index, value) => site.save(`design.checklist.item.${index}`, value)}
      />

      <FeeScheduleSection
        title={site.get(
          "design.fees.title",
          "KNOW YOUR DESIGN FILING COSTS BEFORE YOU SUBMIT"
        )}
        description={site.get(
          "design.fees.description",
          "Review the applicable service fees and official fees before filing."
        )}
        editable={site.editMode}
        onSaveTitle={(value) => site.save("design.fees.title", value)}
        onSaveDescription={(value) => site.save("design.fees.description", value)}
      />

      <LandingTextSection
        heading={site.get("design.notice.heading", "IMPORTANT NOTICE")}
        subheading={site.get("design.notice.subheading", "Your design representation matters")}
        variant="dark"
        editable={site.editMode}
        onSaveHeading={(value) => site.save("design.notice.heading", value)}
        onSaveSubheading={(value) => site.save("design.notice.subheading", value)}
        body={[
          site.get(
            "design.notice.body.0",
            "Please ensure that the views and representations you provide accurately show the design for which protection is sought."
          ),
          site.get(
            "design.notice.body.1",
            "xpress-ip prepares and submits your application based on the information and documents you provide."
          ),
        ]}
        onSaveBody={(index, value) => site.save(`design.notice.body.${index}`, value)}
      />

      <LandingTextSection
        heading={site.get("design.why.heading", "WHY XPRESS-IP?")}
        subheading={site.get(
          "design.why.subheading",
          "Fast online filing. Professional European representation."
        )}
        editable={site.editMode}
        onSaveHeading={(value) => site.save("design.why.heading", value)}
        onSaveSubheading={(value) => site.save("design.why.subheading", value)}
        body={[
          site.get("design.why.body.0", "File your design online from anywhere in the world."),
          site.get(
            "design.why.body.1",
            "We prepare the application, submit it to the EUIPO, pay the official fees after receiving your payment and handle the relevant administrative follow-up."
          ),
        ]}
        onSaveBody={(index, value) => site.save(`design.why.body.${index}`, value)}
      />

      <InstantFilingSection
        heading={site.get("design.services.heading", "INTELLECTUAL PROPERTY RIGHTS")}
        types={serviceCards}
        closingDivider={false}
        editable={site.editMode}
        onSaveHeading={(value) => site.save("design.services.heading", value)}
        onSaveCardTitle={(index, value) => site.save(`design.services.card.${index}.title`, value)}
        onSaveCardText={(index, value) => site.save(`design.services.card.${index}.text`, value)}
      />

      <Footer
        editable={site.editMode}
        email={site.get("design.footer.email", "info@xpress-ip.com")}
        tagline={site.get("design.footer.tagline", "EUROPEAN IP FILING, MADE SIMPLE.")}
        taglineSub={site.get("design.footer.taglineSub", "Patents · Trademarks · Designs")}
        onSaveEmail={(value) => site.save("design.footer.email", value)}
        onSaveTagline={(value) => site.save("design.footer.tagline", value)}
        onSaveTaglineSub={(value) => site.save("design.footer.taglineSub", value)}
      />
    </div>
  )
}
